import { CreateBlock } from '@bechtel/client-blocks';
import {
  bankersRound,
  getDecimalPlaces,
  getUserUnitLabel,
  regularRound,
  SuspenseLoader,
  TextWithEllipsis,
  useAppUser,
  useBlockPermission,
  useDate,
  WorkPackage3dViewer,
} from '@bechtel/client-common';
import { Box, Text, theme, useSideSheetState } from '@bechtel/gds';
import { CommodityType, Material, MaterialCommodity } from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import React, { Suspense } from 'react';
import { CommodityDetail } from '~/components/organisms/commodity-detail';
import { CommodityContext } from '~/detail/commodities/context';
import { useWorkPackage } from '~/detail/context';
import DocumentManager from '~/detail/document-manager/internals';
import { Commodity } from '~/detail/types';
import CommodityAction from '../action';
import { MilestoneList } from '../milestones';
import { CommodityItemInfoStyled, CommodityItemStyled, CommodityTagTextStyled } from './styled';

export interface CommodityItemProps {
  commodity: Commodity;
  collapsed: boolean;
  type: CommodityType;
  isUnitOfMeasureHour: boolean;
  collapseMe: () => void;
}

const CommodityItem: React.FC<CommodityItemProps> = ({
  commodity,
  type,
  collapsed,
  collapseMe,
  isUnitOfMeasureHour,
}) => {
  const workPackage = useWorkPackage();
  const { user } = useAppUser();

  const { canCreateBlock } = useBlockPermission(
    workPackage.assignedCrewMembers,
    workPackage.facilities ? workPackage.facilities : []
  );
  const {
    visible: visibleCreateBlock,
    open: openCreateBlockSideSheet,
    close: closeCreateBlockSideSheet,
  } = useSideSheetState(false);

  const {
    visible: visibleCommodityDetail,
    open: openCommodityDetailSideSheet,
    close: closeCommodityDetailSideSheet,
  } = useSideSheetState(false);
  const {
    visible: showDocumentManager,
    open: openDocumentManager,
    close: closeDocumentManager,
  } = useSideSheetState(false);
  const { visible: show3dViewer, open: open3dViewer, close: close3dViewer } = useSideSheetState(false);
  const { formatISODate } = useDate();
  const { material } = commodity as MaterialCommodity;
  const { mrrNumber, deliveryDate, mwrNumber } = (material as Material) || {};
  const deliveryDateFormated = deliveryDate ? formatISODate(deliveryDate) : '';

  return (
    <CommodityContext id={commodity.id} tag={commodity.tag} typeName={type.name} typeId={type.id} key={commodity.id}>
      <CommodityItemStyled>
        <CommodityItemInfoStyled>
          {/* Max-width defined for larger screen to limit gap between MRR and MWR */}
          <Box flexDirection={{ sm: 'column' }} css={{ minWidth: 0, maxWidth: '50%' }} flexBasis={{ sm: '100%' }}>
            <Box flexDirection={{ sm: 'column' }}>
              <Box py={{ sm: '200' }}>
                <CommodityTagTextStyled family="graphik" weight="600" color="grey.1000" size="400">
                  {commodity.tag}
                </CommodityTagTextStyled>
                <Box
                  alignItems={{ sm: 'center' }}
                  ml={{ sm: '300' }}
                  css={`
                    white-space: nowrap;
                  `}
                >
                  <Text color={'grey.600'} size={'400'} style={{ whiteSpace: 'nowrap' }}>
                    {isUnitOfMeasureHour
                      ? `(${bankersRound(commodity.earnedHours, 0)} / ${bankersRound(commodity.totalEarnableHours, 0)} ${t`Hours`})`
                      : (() => {
                          const decimalPlaces = getDecimalPlaces(commodity.totalEarnableQuantity.quantity);
                          return `(${regularRound(commodity.earnedQuantity, decimalPlaces)} / ${commodity.totalEarnableQuantity.quantity} ${getUserUnitLabel(user)})`;
                        })()}
                  </Text>
                </Box>
              </Box>

              <Box pt={{ sm: '200' }}>
                <Box
                  pr={{ sm: '200' }}
                  css={`
                    min-width: 0; /* required to work flex-basis, if removed flex-basis will not truncate text with ellipses.*/
                  `}
                  alignItems={{ sm: 'center' }}
                >
                  <Text weight="600" size="300" color="grey.600">
                    <Trans>MRR:</Trans>
                  </Text>
                  <TextWithEllipsis
                    size="300"
                    color="grey.600"
                    style={{ marginLeft: `${theme.spacings[200]}` }}
                    data-testid="mrrtext"
                    title={`${mrrNumber}`}
                  >
                    {mrrNumber ? `${mrrNumber}` : t`Not Available`}
                  </TextWithEllipsis>
                </Box>
                <Box
                  pl={{ sm: '200' }}
                  css={`
                    min-width: 0; /* required to work flex-basis, if removed flex-basis will not truncate text with ellipses.*/
                  `}
                  alignItems={{ sm: 'center' }}
                >
                  <Text weight="600" size="300" color="grey.600">
                    <Trans> MWR:</Trans>
                  </Text>
                  <TextWithEllipsis
                    size="300"
                    color="grey.600"
                    style={{ marginLeft: `${theme.spacings[200]}` }}
                    data-testid="mwrtext"
                    title={`${mwrNumber}`}
                  >
                    {mwrNumber ? `${mwrNumber}` : t`Not Available`}
                  </TextWithEllipsis>
                </Box>
                <Box
                  pl={{ sm: '200' }}
                  css={`
                    min-width: 0; /* required to work flex-basis, if removed flex-basis will not truncate text with ellipses.*/
                  `}
                  alignItems={{ sm: 'center' }}
                >
                  <Text weight="600" size="300" color="grey.600">
                    <Trans> FCD:</Trans>
                  </Text>
                  <TextWithEllipsis
                    size="300"
                    color="grey.600"
                    style={{ marginLeft: `${theme.spacings[200]}` }}
                    data-testid="mwrtext"
                    title={`${deliveryDateFormated}`}
                  >
                    {deliveryDateFormated ? `${deliveryDateFormated}` : t`Not Available`}
                  </TextWithEllipsis>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box alignItems={{ sm: 'center' }} pt={{ sm: '400' }}>
            <CommodityAction icon="list" iconText={t`Details`} onClick={openCommodityDetailSideSheet} />
            {visibleCommodityDetail && (
              <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
                <CommodityDetail
                  commodityId={commodity.id}
                  commodityName={type.name}
                  commodityTag={commodity.tag}
                  workPackageTag={workPackage.tag}
                  close={closeCommodityDetailSideSheet}
                />
              </Suspense>
            )}
            <CommodityAction icon="stopwatch" iconText={t`Time`} disabled={true} />
            {/* <CommodityAction icon="cube" iconText={t`3D`} onClick={open3dViewer} /> */}
            <CommodityAction
              icon="ruler-combined"
              iconText={t`ISO`}
              disabled={!commodity.isoDocument}
              onClick={openDocumentManager}
            />
            {canCreateBlock && (
              <CommodityAction icon="exclamation-triangle" iconText={t`Block`} onClick={openCreateBlockSideSheet} />
            )}
            <CommodityAction
              icon="compress-alt"
              iconText={collapsed ? t`Expand` : t`Collapse`}
              onClick={() => collapseMe && collapseMe()}
            />
          </Box>
        </CommodityItemInfoStyled>

        {visibleCreateBlock && (
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <CreateBlock
              workPackageId={workPackage.id}
              workPackageTag={workPackage.tag}
              assignedCrewMembers={workPackage.assignedCrewMembers}
              facilities={workPackage.facilities ? workPackage.facilities : []}
              close={closeCreateBlockSideSheet}
              // @ts-ignore commodity conversion to commodity array
              commodities={[commodity]}
              multiBlock={false}
            />
          </Suspense>
        )}

        {showDocumentManager && (
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <DocumentManager
              workPackageId={workPackage.id}
              onClose={closeDocumentManager}
              selectedTag={commodity.tag}
              selectedDrawingNo={commodity.isoDocument?.documentNumber}
            />
          </Suspense>
        )}
        {show3dViewer && (
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <WorkPackage3dViewer
              workPackageTag={workPackage.tag}
              commodityName={commodity.type.name}
              commodityTag={commodity.tag}
              onClose={close3dViewer}
            />
          </Suspense>
        )}

        {commodity.milestones && commodity.milestones.length > 0 && (
          <MilestoneList
            milestones={commodity.milestones}
            activityHistory={commodity.milestonesActivityHistory?.items ?? []}
            hidden={collapsed}
            isUnitOfMeasureHour={isUnitOfMeasureHour}
          />
        )}
      </CommodityItemStyled>
    </CommodityContext>
  );
};

export default CommodityItem;
